import { isBackwardActiveSubStatusMove, leadStatusRank, type LeadDetail } from '@utazzo/shared';
import {
  UzBadge,
  UzButton,
  UzCheckbox,
  UzDateTimePicker,
  UzDialog,
  UzNumberInput,
  UzSelect,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { missingForActiveLead } from '../active-readiness';
import {
  canCloseLeadAsLostDirectly,
  canReviewLeadApprovalRequest,
  resolvePendingLeadApproval,
} from '../lead-approval-access';
import * as leadsApi from '../leads.api';
import { formatStatusWithSubStatus } from '../stage-label';
import {
  useQueryStatusOptionsWithBucket,
  useQuerySubStatusOptionsForParent,
} from '../use-lead-options';
import { useCreateStatusChangeRequest, useTransitionLeadStage, useUpdateLead } from '../use-leads';

import { LeadApprovalReviewDialog, toApprovalInboxItem } from './LeadApprovalReviewDialog';
import { LeadMissingFieldsModal } from './LeadMissingFieldsModal';

import { useCurrentUser } from '@/features/auth/use-auth';
import { useItineraryVersions } from '@/features/itineraries/use-itineraries';
import { useLostReasonsList } from '@/features/lost-reasons/use-lost-reasons';
import { useAppConfig } from '@/features/settings/use-settings';

// Plain "Rs" prefix rather than the Intl currency symbol - this label feeds a
// native <select> option, where the rupee glyph (Intl.NumberFormat currency
// INR) can render as tofu in some OS-native dropdown fonts. Same paise/100 +
// en-IN grouping as the itinerary pricing components' formatters.
const fmtWonPrice = (paise: string): string =>
  `Rs ${Math.round(Number(paise) / 100).toLocaleString('en-IN')}`;

/**
 * Inline status changer (ADR-0054). Sits in the lead detail header before
 * "Create Itinerary". Renders four moving parts:
 *
 *   - Status dropdown - query_statuses (current status included so
 *     the picker matches the header badge; `won` and `long_followup`
 *     remain excluded as manual change targets).
 *   - Sub-status dropdown - only when the picked status has children;
 *     defaults to `proposal` when the picked status is `active`.
 *   - Lost-reason picker - required when picked status is in LOST.
 *   - Submit button - dispatches to one of:
 *       * `useTransitionLeadStage`         (admin happy path, or
 *                                            non-LOST transitions)
 *       * `useCreateStatusChangeRequest`   (non-admin → LOST, creates
 *                                            a PENDING approval row)
 *
 * Three guard rails layered around the submit:
 *
 *   - Not Qualified → Active without the required-fields set: opens a
 *     modal listing what's missing instead of POSTing. The server
 *     enforces the same rule so a stale UI build can't slip past.
 *   - Reopening a closed lead (LOST or WON) is ADMIN-direct only (server
 *     returns 403 otherwise; we hide the dropdown). Non-admins
 *     (SALES_LEAD, SALES_AGENT) instead get a "Request reopen" action
 *     that files a STATUS_CHANGE approval request (2026-08-06) rather
 *     than a dead end.
 *   - When a PENDING request already exists for this lead, a small
 *     "Awaiting approval" cue is shown. Reviewers get a "Review
 *     request" action that opens the shared approvals dialog inline
 *     (2026-08-01) - same modal, same mutations as `/leads/approvals`.
 *
 * The lead Edit form is intentionally untouched - the operator clicks
 * Edit for everything else; THIS panel is the dedicated path for
 * status changes from outside the form. ADR-0054.
 */

interface LeadStatusChangerProps {
  lead: LeadDetail;
}

export const LeadStatusChanger = ({ lead }: LeadStatusChangerProps): React.JSX.Element | null => {
  const me = useCurrentUser();
  // 2026-08-06. SALES_LEAD approval parity: only ADMIN acts on the
  // pipeline directly now (LOST / WON / reopen / backward moves all
  // route SALES_LEAD through the same approval workflow as
  // SALES_AGENT). Reverses the 2026-07-14 / 2026-07-25 SALES_LEAD-direct
  // behavior. `canCloseLeadAsLostDirectly` mirrors the server's
  // ADMIN-only direct-transition predicate; reused here as the single
  // "can I act directly" decision for lost/won/reopen so this component
  // can't drift from the server gate.
  const canActDirectly = canCloseLeadAsLostDirectly(me);
  const transition = useTransitionLeadStage();
  const requestChange = useCreateStatusChangeRequest();
  const updateLead = useUpdateLead();
  const pending = transition.isPending || requestChange.isPending || updateLead.isPending;

  const stagesQuery = useQueryStatusOptionsWithBucket();
  const travelIntentEnforce = useAppConfig().data?.leads.travelIntentEnforce ?? false;

  const [statusId, setStatusId] = useState<string>(() => lead.currentStatus.id);
  const [subStatusId, setSubStatusId] = useState<string>(() => lead.currentSubStatus?.id ?? '');
  // Seed from the lead's existing lost reason so a lead already in LOST
  // shows its captured reason in the picker (read-back), instead of an
  // empty "Lost reason" placeholder. Kept in sync on nav / refetch below.
  const [lostReasonId, setLostReasonId] = useState<string>(() => lead.lostReason?.id ?? '');
  const [note, setNote] = useState<string>('');
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [showWonModal, setShowWonModal] = useState(false);
  const [showLostDialog, setShowLostDialog] = useState(false);
  // 2026-07-13. Hot sub-status capture. Opens whenever the operator
  // moves the lead into Active / Hot without an expected payment date
  // already on the lead. The date is captured here and sent alongside
  // the transition so the two land atomically. `hotSavedRef` prevents
  // the picker revert when the operator saves + we're closing the
  // dialog ourselves.
  const [showHotDialog, setShowHotDialog] = useState(false);
  const [hotExpectedPaymentDate, setHotExpectedPaymentDate] = useState<Date | null>(null);
  // 2026-08-06. Budget capture on Lost with a "requires budget" reason (e.g.
  // "Low budget"). Opens when the picked reason requiresBudget and the lead
  // has no budget. The value rides along the direct transition; on the agent
  // approval path it is saved onto the lead first, then the request is sent.
  const [showBudgetDialog, setShowBudgetDialog] = useState(false);
  const [lostBudget, setLostBudget] = useState<number | null>(null);
  const budgetSavedRef = useRef(false);
  // 2026-08-01. In-place review of THIS lead's pending request - opens
  // the same modal the approvals inbox uses, so a reviewer sitting on
  // the lead can verify the context and approve / reject without a
  // detour. Approving writes the fresh lead into the cache and
  // invalidates the approvals queries, so the inbox + its pending
  // badge follow along.
  const [showApprovalReview, setShowApprovalReview] = useState(false);

  const firingRef = useRef(false);
  const submittedTargetRef = useRef<string>('');
  // 2026-07-10. True only after the OPERATOR moved one of the pickers.
  // The auto-submit effect below cannot otherwise tell "the user picked
  // a new stage" apart from "the lead prop changed under me", and the
  // `submittedTargetRef` guard is useless for the latter: the sync
  // effect writes that ref synchronously in the same commit, so the
  // auto-submit effect - which still closes over the PREVIOUS render's
  // `statusId` - sees a guaranteed mismatch and fires a transition back
  // to the stale pick. That is what silently reverted a lead to Not
  // Qualified one second after the Follow-up tab's "Move to Active".
  // Cleared by `syncPickerFromLead` + the sync effect, i.e. whenever the
  // picker is driven by the server rather than by a click.
  const userPickedRef = useRef(false);
  // True only while the missing-fields modal is closing because of a
  // successful in-modal save (vs a user dismiss). On save we keep the
  // picker on "Active" so the auto-submit completes the move once the lead
  // is ready; on dismiss we revert the picker so the trigger doesn't stay
  // stuck showing "Active" (the bug this guards against).
  const savedFromMissingRef = useRef(false);
  // 2026-07-13. True only while the hot-payment-date dialog is closing
  // because of a successful save (vs a user dismiss). On save we let
  // the picker stay on the new stage and complete the transition; on
  // dismiss we revert so the trigger doesn't get stuck on "Hot".
  const hotSavedRef = useRef(false);

  // Keep the picker aligned with the lead's live stage when navigating
  // between leads or after a transition writes the fresh detail into
  // the query cache. Skipped while a transition is in flight so a
  // stale `lead` prop cannot revert the operator's pick and re-trigger
  // auto-submit.
  //
  // 2026-07-08. `pending` was dropped from the dep list. When a
  // transition FAILED (server 400 / 500) `pending` toggled true -> false
  // but `lead.currentStatus.id` didn't change (cache is only written
  // on success). The effect used to re-fire on the pending flip and
  // reset the picker back to the OLD status - snapping the operator's
  // pick away even though the change hadn't been applied yet. Now the
  // effect only fires when the lead itself actually changes (nav or
  // successful transition), so a failed transition leaves the pick
  // visible for the operator to fix or retry.
  useEffect(() => {
    if (pending || firingRef.current) return;
    setStatusId(lead.currentStatus.id);
    setSubStatusId(lead.currentSubStatus?.id ?? '');
    // Read back the lead's persisted lost reason (present only for LOST
    // leads) so the picker mirrors the header state.
    const seededLostReason = lead.lostReason?.id ?? '';
    setLostReasonId(seededLostReason);
    submittedTargetRef.current = `${lead.currentStatus.id}|${lead.currentSubStatus?.id ?? ''}|${seededLostReason}`;
    // The picker is being driven by the server (nav to another lead, or
    // a transition fired from somewhere else on the page such as the
    // Follow-up tab). Disarm auto-submit: nothing here is an operator
    // intent to move the lead.
    userPickedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id, lead.currentStatus.id, lead.currentSubStatus?.id, lead.lostReason?.id]);

  // Always include the lead's current status so the dropdown shows the
  // same value as the header badge. `won` is now a selectable target
  // (picking it opens the lead Edit form instead of auto-transitioning -
  // see the `pickedIsWon` guard in submit()). `long_followup` stays
  // excluded (auto-promotion rule).
  const statusOptions = useMemo(() => {
    return stagesQuery.data
      .filter((s) => s.id === lead.currentStatus.id || s.code !== 'long_followup')
      .map((s) => ({ value: s.id, label: s.name }));
  }, [stagesQuery.data, lead.currentStatus.id]);

  const pickedStatus = useMemo(
    () => stagesQuery.data.find((s) => s.id === statusId) ?? null,
    [stagesQuery.data, statusId],
  );
  const pickedIsLost = pickedStatus?.bucket === 'LOST';
  const pickedIsActive = pickedStatus?.code === 'active';
  const pickedIsWon = pickedStatus?.code === 'won';
  const isFresh = lead.currentStatus.code === 'not_qualified';
  const isInLost = lead.currentBucket === 'LOST';
  // 2026-08-06. Closed-WON counterpart to `isInLost` - both buckets are
  // ADMIN-direct-only to reopen; non-admins get the same reopen-request
  // affordance below rather than a dead end (or, before this change, no
  // changer at all - `LeadDetailPage` used to skip mounting this component
  // outright for a non-admin on a WON lead).
  const isInWon = lead.currentBucket === 'WON';
  // 2026-07-09. Gate the top-level status dropdown when the lead is
  // still `not_qualified`: a Not Qualified -> Active move must go via
  // the Follow-up flow (Call Status = Connected + Call Sub-Status =
  // "Move to Active"), not the header dropdown. Everything else is a
  // no-op until at least one COMPLETED follow-up on the lead has
  // `result === 'CONNECTED'`. The dropdown reopens automatically once
  // that condition holds, so no operator round-trip through refresh is
  // needed.
  const hasConnectedFollowUp = useMemo(
    () => (lead.followUps ?? []).some((f) => f.result === 'CONNECTED'),
    [lead.followUps],
  );
  const dropdownLocked = isFresh && !hasConnectedFollowUp;
  // The canonical WON status id (the picker excludes `won` visually but we
  // still need its id to drive the win transition / approval request).
  const wonStatusId = useMemo(
    () => stagesQuery.data.find((s) => s.code === 'won')?.id ?? null,
    [stagesQuery.data],
  );
  // 2026-08-06. Reopen target for the non-admin "Request reopen" action
  // below - a closed (LOST/WON) lead reopens back to Not Qualified, the
  // same entry point every fresh lead starts at.
  const notQualifiedStatusId = useMemo(
    () => stagesQuery.data.find((s) => s.code === 'not_qualified')?.id ?? null,
    [stagesQuery.data],
  );

  const subStatusesQuery = useQuerySubStatusOptionsForParent(statusId || null);
  // 2026-07-13. Resolve the picked sub-status code so submit() can
  // detect the Active + Hot combination and force the expected
  // payment date capture flow when the lead doesn't already carry one.
  const pickedSubStatusCode = useMemo(
    () => subStatusesQuery.data.find((s) => s.id === subStatusId)?.code ?? null,
    [subStatusesQuery.data, subStatusId],
  );
  const pickedIsHot = pickedIsActive && pickedSubStatusCode === 'hot';

  const hasStageChange =
    statusId !== lead.currentStatus.id ||
    (subStatusId || null) !== (lead.currentSubStatus?.id ?? null);

  // Default sub-status to `proposal` when Active is freshly picked
  // (operator changed parent to active with no sub yet).
  useEffect(() => {
    if (!pickedIsActive) return;
    if (subStatusId) return;
    const proposal = subStatusesQuery.data.find((s) => s.code === 'proposal');
    if (proposal) setSubStatusId(proposal.id);
  }, [pickedIsActive, subStatusId, subStatusesQuery.data]);

  const lostReasonsQuery = useLostReasonsList({
    page: 1,
    pageSize: 50,
    sortDir: 'asc',
    sortBy: 'sortOrder',
    isActive: true,
  });
  const lostReasonOptions = useMemo(
    () =>
      // System/automatic-only reasons (e.g. "Not Contactable Loss(Nq)") are set
      // by the cron only - never offered for manual selection.
      (lostReasonsQuery.data?.data ?? [])
        .filter((r) => !r.isAutoOnly)
        .map((r) => ({
          value: r.id,
          label: r.name,
        })),
    [lostReasonsQuery.data],
  );
  // The picked lost reason + whether it forces a budget on a budget-less lead.
  const pickedLostReason = useMemo(
    () => (lostReasonsQuery.data?.data ?? []).find((r) => r.id === lostReasonId) ?? null,
    [lostReasonsQuery.data, lostReasonId],
  );
  const leadHasBudget = typeof lead.budgetAmount === 'number' && lead.budgetAmount > 0;
  const needsBudgetForLost = Boolean(
    pickedIsLost && pickedLostReason?.requiresBudget && !leadHasBudget,
  );

  // Picker-tuple readiness: status changed + any required dependent picker
  // filled. Drives the reactive auto-submit effect below - for a LOST pick
  // this is deliberately note-independent, because for a non-admin it's
  // what OPENS the request dialog (where the note is actually typed); the
  // note doesn't exist yet at that point.
  const hasRequiredPicks =
    hasStageChange && statusId !== '' && (!pickedIsLost || lostReasonId !== '');
  // Full submit-readiness, including the note. A LOST pick (direct
  // transition or approval request) needs a non-empty note - the server
  // now rejects both a direct into-LOST transition and a LOST_TRANSITION
  // request without one. Used to gate the actual submit actions (the
  // ADMIN "Confirm lost" button, the request dialog's "Send for
  // approval" button) - never the reactive effect itself, since typing
  // into a free-text field would otherwise auto-fire the mutation after
  // the first keystroke.
  const canSubmit = hasRequiredPicks && (!pickedIsLost || note.trim() !== '');

  const syncPickerFromLead = (detail: LeadDetail): void => {
    setStatusId(detail.currentStatus.id);
    setSubStatusId(detail.currentSubStatus?.id ?? '');
    // Mirror the persisted lost reason (LOST leads only) rather than
    // clearing it, so the read-back survives an explicit re-sync.
    const seededLostReason = detail.lostReason?.id ?? '';
    setLostReasonId(seededLostReason);
    setNote('');
    submittedTargetRef.current = `${detail.currentStatus.id}|${detail.currentSubStatus?.id ?? ''}|${seededLostReason}`;
    userPickedRef.current = false;
  };

  const reset = (): void => {
    syncPickerFromLead(lead);
  };

  // ----- Auto-submit (2026-06-25) -------------------------------------
  // Replaces the previous Submit button. As soon as the operator's
  // current pick set is "ready" (status picked + any required
  // dependent picker filled), we trigger the same `submit()` path
  // the button used to. `firingRef` blocks re-entry while a submit
  // is in flight so a re-render or a fast pick-then-change can't
  // double-fire the mutation. `submittedTargetRef` remembers the
  // exact picker-tuple we just submitted so the effect doesn't loop
  // on the same state until the user changes it.
  useEffect(() => {
    // Deliberately `hasRequiredPicks`, not `canSubmit` - a LOST pick still
    // needs to reach `submit()` below with an empty note so a non-admin's
    // request dialog opens (that's where the note is typed). `submit()`
    // itself never completes a LOST mutation off this path; see the next
    // guard and `submit()`'s own dialog-opening branch.
    if (!hasRequiredPicks) return;
    if (pending) return;
    if (firingRef.current) return;
    // The ADMIN direct-into-LOST path needs a free-text note (server
    // requires one on a direct into-LOST transition). Auto-firing off a
    // free-text field would submit after the operator's very first
    // keystroke, cutting the note off mid-sentence - so this one
    // combination is excluded here and submitted from an explicit
    // "Confirm" button instead (see `confirmLostDirect` below).
    if (pickedIsLost && canActDirectly) return;
    // Only an operator pick may dispatch a transition. Without this the
    // effect also fires when `lead` changes beneath a stale picker and
    // moves the lead BACK to the stage it just left.
    if (!userPickedRef.current) return;
    // 2026-06-25 - Active needs the sub-status defaulting effect to
    // settle FIRST. The default-proposal effect runs after the
    // status picker resolves; skipping this guard would auto-submit
    // with subStatusId=''. The sub-status effect will fire this
    // effect again once the default lands.
    if (pickedIsActive && !subStatusId && subStatusesQuery.options.length > 0) return;
    const targetKey = `${statusId}|${subStatusId}|${lostReasonId}`;
    if (submittedTargetRef.current === targetKey) return;
    submittedTargetRef.current = targetKey;
    firingRef.current = true;
    void submit().finally(() => {
      firingRef.current = false;
    });
    // `submit` is stable enough to omit from deps - it's recreated
    // on every render but only reads current state via the picker
    // closures. Including it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasRequiredPicks,
    pending,
    statusId,
    subStatusId,
    lostReasonId,
    hasStageChange,
    pickedIsActive,
    subStatusesQuery.options.length,
    pickedIsLost,
    canActDirectly,
  ]);

  // ----- Submit logic --------------------------------------------------

  const submit = async (): Promise<void> => {
    // Guard 1: not_qualified → active requires the readiness set. Show
    // the modal listing missing fields. The Edit button on the modal
    // points the operator at the lead Edit form.
    // 2026-07-08. Refetch the lead directly from the server BEFORE
    // running the readiness check so a just-saved PATCH's persisted
    // state (dates, budget, ...) is used instead of the potentially
    // stale `lead` prop. This eliminates the loop where the client
    // check saw fresh cache, fired the transition, and the server
    // then 400'd because it read fresh DB values that disagreed with
    // whatever the modal-embedded LeadFormPage actually saved.
    if (isFresh && pickedIsActive) {
      let fresh: LeadDetail = lead;
      try {
        fresh = await leadsApi.getLead(lead.id);
      } catch {
        // Network / auth failure - fall back to the stale prop; if the
        // transition fires and 400s, our catch handler still surfaces
        // the server's missing list.
      }
      // Include travel-intent predicates in the pre-check when the server
      // will enforce them - keeps client + server in agreement so the
      // operator sees the modal here rather than after a 400.
      const missing = missingForActiveLead(fresh, {
        includeTravelIntent: travelIntentEnforce,
      });
      if (missing.length > 0) {
        setShowMissingModal(true);
        submittedTargetRef.current = '';
        return;
      }
    }

    // Guard: moving to Won opens the lead Edit form (the operator
    // completes the win details there). We deliberately do NOT
    // auto-transition to won here - the follow-up flow is wired from
    // the form. Reset the submitted-target so re-picking Won reopens it.
    if (pickedIsWon) {
      setShowWonModal(true);
      submittedTargetRef.current = '';
      return;
    }

    // Guard 2: LOST transitions for SALES_AGENT and SALES_LEAD go
    // through the approval workflow. Only ADMIN moves directly.
    if (pickedIsLost && !canActDirectly) {
      setShowLostDialog(true);
      submittedTargetRef.current = '';
      return;
    }

    // Sales-agent approval rule (2026-07-06; v3 2026-07-13; v4 2026-07-19):
    // pipeline stages are not_qualified (0) -> long_followup (1) ->
    // active (2, walking Proposal -> Warm -> Hot) -> won / lost. An agent
    // moves DIRECTLY only FORWARD or laterally within OPEN. Any BACKWARD
    // move routes through the reviewer inbox - both across statuses
    // (Active -> Not Qualified / Long Follow-up, Long Follow-up -> Not
    // Qualified) and, as of v4, DOWN the Active sub-status ladder
    // (Hot -> Warm -> Proposal). Won/Lost are handled by their dedicated
    // guards above. Ranks come from @utazzo/shared so this pre-flight check
    // cannot drift from the server's Gate 2c, which enforces the same rule.
    const fromRank = leadStatusRank(lead.currentStatus.code);
    const toRank = pickedStatus ? leadStatusRank(pickedStatus.code) : null;
    const isBackwardOpenMove =
      fromRank !== null && toRank !== null && toRank < fromRank && pickedStatus?.bucket === 'OPEN';
    const isTargetingActive = pickedIsActive;
    const isSameStatus = pickedStatus?.id === lead.currentStatus.id;
    const isUnclassifiedNonActiveMove =
      !isTargetingActive && !isSameStatus && (fromRank === null || toRank === null);
    // 2026-07-19 (v4). Demotion inside Active - Hot -> Warm, Hot -> Proposal,
    // Warm -> Proposal. Same parent status, so the rank comparison above reads
    // them as lateral; the sub-status ladder is what catches them. Mirrors
    // Gate 2c server-side, off the same shared ranks.
    const isBackwardActiveSubMove =
      isSameStatus &&
      isTargetingActive &&
      isBackwardActiveSubStatusMove(lead.currentSubStatus?.code ?? null, pickedSubStatusCode);
    // Branch scoping mirrors Gate 2c server-side (2026-08-06: widened to
    // SALES_LEAD alongside SALES_AGENT for both branches - their request
    // routes to their reporting manager, else the admin pool). ADMIN moves
    // directly.
    const isCrossStatusGated = me?.role === 'SALES_AGENT' || me?.role === 'SALES_LEAD';
    const isSubStatusGated = me?.role === 'SALES_AGENT' || me?.role === 'SALES_LEAD';
    if (
      ((isCrossStatusGated && (isBackwardOpenMove || isUnclassifiedNonActiveMove)) ||
        (isSubStatusGated && isBackwardActiveSubMove)) &&
      pickedStatus
    ) {
      try {
        const updated = await requestChange.mutateAsync({
          id: lead.id,
          payload: {
            kind: 'STATUS_CHANGE',
            toStatusId: pickedStatus.id,
            toSubStatusId: subStatusId || null,
          },
        });
        // A SALES_LEAD's own request has no team lead above it - it routes to
        // the admin pool (resolveLeadApprovalReviewerId returns null for any
        // non-agent requester), so the toast must not promise a team lead.
        toast.success(
          me?.role === 'SALES_LEAD'
            ? 'Sent to an admin for approval'
            : 'Sent to your team lead for approval',
        );
        syncPickerFromLead(updated);
      } catch {
        submittedTargetRef.current = '';
        // toast surfaced by the mutation's onError.
      }
      return;
    }

    // Default path: direct transition.
    if (!pickedStatus) return;
    // 2026-07-13. Hot sub-status capture. If the operator is moving the
    // lead into Active / Hot and there's no expected payment date on
    // the lead yet, open the dialog and pause the submit. The dialog's
    // Save handler (`submitHotDate`) fires the transition directly
    // with the captured date - it does NOT re-enter this submit(), so
    // no ride-along-ref plumbing is needed here. Server enforces the
    // same rule with error code `hot_requires_expected_payment_date`
    // as defence in depth.
    if (pickedIsHot && !lead.expectedPaymentDate) {
      setShowHotDialog(true);
      submittedTargetRef.current = '';
      return;
    }
    // 2026-08-06. Lost with a "requires budget" reason on a budget-less lead:
    // pause and open the budget-capture dialog. The captured budget rides along
    // the transition (submitLostWithBudget). Server enforces the same rule via
    // `lost_requires_budget` as defence in depth.
    if (needsBudgetForLost) {
      setShowBudgetDialog(true);
      submittedTargetRef.current = '';
      return;
    }
    // 2026-06-25: snapshot the lead's current stage BEFORE the
    // transition so the "Undo" toast can fire a reverse transition
    // if the operator mis-picked. Captured here (closure-locked)
    // rather than read from the lead prop inside the toast handler
    // because the lead prop will have re-rendered with the new
    // status by the time the operator clicks Undo.
    const prevStatusId = lead.currentStatus.id;
    const prevStatusName = lead.currentStatus.name;
    const prevSubStatusId = lead.currentSubStatus?.id ?? null;
    const prevSubStatusName = lead.currentSubStatus?.name ?? null;
    const newStatusName = pickedStatus.name;
    const newSubStatusName = subStatusesQuery.data.find((s) => s.id === subStatusId)?.name ?? null;
    try {
      const updated = await transition.mutateAsync({
        id: lead.id,
        suppressSuccessToast: true,
        payload: {
          toStatusId: pickedStatus.id,
          toSubStatusId: subStatusId || null,
          lostReasonId: pickedIsLost ? lostReasonId || null : null,
          // Gated on `pickedIsLost` like `lostReasonId` above - `note` is
          // stateful and outlives a single pick (the operator can type a
          // Lost remark, then re-pick a non-Lost status without it being
          // cleared). Without this gate a stale Lost remark could attach
          // to an unrelated transition's audit trail.
          note: pickedIsLost && note.trim() ? note.trim() : null,
        },
      });
      // Sync from the server response - not the stale `lead` prop.
      // Using `reset()` here briefly reverted the picker to the old
      // sub-status and re-triggered auto-submit in a loop.
      syncPickerFromLead(updated);
      // 2026-06-25 (ops): show a brief Undo toast so the operator
      // can reverse a mis-pick within 8 seconds. Skipped for LOST
      // transitions - those carry a lost-reason + close the lead,
      // and reverting them is a bigger conceptual operation that
      // belongs in the dedicated re-open flow.
      if (!pickedIsLost) {
        const targetLabel = newSubStatusName
          ? `${newStatusName} / ${newSubStatusName}`
          : newStatusName;
        const previousLabel = prevSubStatusName
          ? `${prevStatusName} / ${prevSubStatusName}`
          : prevStatusName;
        toast.success(`Moved to ${targetLabel}`, {
          id: `lead-stage-${lead.id}`,
          description: `Was ${previousLabel}. Undo within 8s if this wasn't intended.`,
          duration: 8000,
          action: {
            label: 'Undo',
            onClick: () => {
              submittedTargetRef.current = '';
              void transition
                .mutateAsync({
                  id: lead.id,
                  suppressSuccessToast: true,
                  payload: {
                    toStatusId: prevStatusId,
                    toSubStatusId: prevSubStatusId,
                    lostReasonId: null,
                    note: 'Reverted via undo within the 8s window.',
                  },
                })
                .then((reverted) => {
                  syncPickerFromLead(reverted);
                })
                .catch(() => {
                  // toast surfaced by the mutation's onError.
                });
            },
          },
        });
      }
    } catch (err) {
      // 2026-07-08. IMPORTANT: do NOT clear `submittedTargetRef` here.
      // Clearing it caused the auto-submit useEffect to fire again on
      // the next re-render (deps unchanged, targetKey vs '' mismatch),
      // re-running the same failing transition in a loop. Keeping the
      // ref pointed at the last-tried tuple means the effect no-ops
      // until the operator picks something DIFFERENT - which is when
      // a retry makes sense.
      // If the server rejected with `lead_not_active_ready`, reopen the
      // missing-fields modal so the operator can fix the gaps in-place.
      // The specific toast (with the server's exact `Missing: ...`
      // list) + input-focus is fired by the mutation's onError via
      // `extractServerError` -> `focusFormField` - no duplicate toast
      // needed here.
      const apiErr = err as { code?: string } | undefined;
      if (apiErr?.code === 'lead_not_active_ready') {
        setShowMissingModal(true);
        return;
      }
      // 2026-07-13. Server-side defence-in-depth for the Hot capture
      // rule. If the client somehow POSTed the transition without a
      // date (stale bundle, direct API call), the server returns this
      // typed error and we open the capture dialog so the operator
      // can supply it in place.
      if (apiErr?.code === 'hot_requires_expected_payment_date') {
        setShowHotDialog(true);
        return;
      }
      // 2026-08-06. Server-side defence-in-depth for the Lost budget rule.
      if (apiErr?.code === 'lost_requires_budget') {
        setShowBudgetDialog(true);
        return;
      }
      // Every other error's toast + focus is handled by the mutation.
    }
  };

  // 2026-07-13. Save handler for the Hot-payment-date dialog. Fires
  // the transition DIRECTLY (rather than re-entering submit() through
  // a ref) so the ride-along `expectedPaymentDate` is captured in the
  // same closure that builds the mutation payload - no chance for a
  // subsequent re-render / effect run to see a null ref.
  const submitHotDate = async (): Promise<void> => {
    if (!hotExpectedPaymentDate || !pickedStatus) return;
    const d = hotExpectedPaymentDate;
    const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    // Freeze the picker snapshot so the undo toast can revert cleanly
    // if the operator mis-picked - same pattern as the default submit
    // path below.
    const prevStatusId = lead.currentStatus.id;
    const prevStatusName = lead.currentStatus.name;
    const prevSubStatusId = lead.currentSubStatus?.id ?? null;
    const prevSubStatusName = lead.currentSubStatus?.name ?? null;
    const newStatusName = pickedStatus.name;
    const newSubStatusName = subStatusesQuery.data.find((s) => s.id === subStatusId)?.name ?? null;
    hotSavedRef.current = true;
    firingRef.current = true;
    try {
      const updated = await transition.mutateAsync({
        id: lead.id,
        suppressSuccessToast: true,
        payload: {
          toStatusId: pickedStatus.id,
          toSubStatusId: subStatusId || null,
          lostReasonId: null,
          note: null,
          expectedPaymentDate: ymd,
        },
      });
      setShowHotDialog(false);
      setHotExpectedPaymentDate(null);
      syncPickerFromLead(updated);
      const targetLabel = newSubStatusName
        ? `${newStatusName} / ${newSubStatusName}`
        : newStatusName;
      const previousLabel = prevSubStatusName
        ? `${prevStatusName} / ${prevSubStatusName}`
        : prevStatusName;
      toast.success(`Moved to ${targetLabel}`, {
        id: `lead-stage-${lead.id}`,
        description: `Was ${previousLabel}. Undo within 8s if this wasn't intended.`,
        duration: 8000,
        action: {
          label: 'Undo',
          onClick: () => {
            submittedTargetRef.current = '';
            void transition
              .mutateAsync({
                id: lead.id,
                suppressSuccessToast: true,
                payload: {
                  toStatusId: prevStatusId,
                  toSubStatusId: prevSubStatusId,
                  lostReasonId: null,
                  note: 'Reverted via undo within the 8s window.',
                },
              })
              .then((reverted) => syncPickerFromLead(reverted))
              .catch(() => {
                // toast surfaced by the mutation's onError.
              });
          },
        },
      });
    } catch {
      // Toast + focus handled by the mutation's onError. Leave the
      // dialog open so the operator can retry with the same date.
    } finally {
      firingRef.current = false;
    }
  };

  // 2026-08-06. Save handler for the Lost budget-capture dialog (direct path).
  // Fires the transition with the captured budget riding along so budget + the
  // Lost move land in one round-trip (mirrors submitHotDate). No undo toast -
  // Lost carries a reason and closes the lead.
  const submitLostWithBudget = async (): Promise<void> => {
    if (!pickedStatus || lostBudget === null || lostBudget <= 0) return;
    budgetSavedRef.current = true;
    firingRef.current = true;
    try {
      const updated = await transition.mutateAsync({
        id: lead.id,
        suppressSuccessToast: true,
        payload: {
          toStatusId: pickedStatus.id,
          toSubStatusId: subStatusId || null,
          lostReasonId: lostReasonId || null,
          note: note.trim() ? note.trim() : null,
          budgetAmount: lostBudget,
        },
      });
      setShowBudgetDialog(false);
      setLostBudget(null);
      syncPickerFromLead(updated);
      toast.success(`Marked ${pickedStatus.name}`);
    } catch {
      // Toast + focus handled by the mutation's onError. Leave the dialog open.
    } finally {
      firingRef.current = false;
    }
  };

  // Explicit confirm for the ADMIN direct-into-LOST path (see the
  // auto-submit effect above for why this can't be reactive off the
  // note textarea). Mirrors the auto-submit effect's guard sequence -
  // `firingRef`/`submittedTargetRef` bookkeeping keeps a stray re-render
  // from double-firing while the mutation is in flight.
  const confirmLostDirect = async (): Promise<void> => {
    if (!canSubmit || pending || firingRef.current) return;
    submittedTargetRef.current = `${statusId}|${subStatusId}|${lostReasonId}`;
    firingRef.current = true;
    try {
      await submit();
    } finally {
      firingRef.current = false;
    }
  };

  const submitLostRequest = async (): Promise<void> => {
    if (!pickedStatus) return;
    // 2026-08-06. A "requires budget" reason on a budget-less lead: the agent
    // must capture a budget. The approval request can't carry it, so save it
    // onto the lead FIRST, then submit the request (the server's
    // `lost_requires_budget` gate then passes on the reviewer's approve-replay).
    if (needsBudgetForLost && (lostBudget === null || lostBudget <= 0)) return;
    try {
      if (needsBudgetForLost && lostBudget !== null) {
        await updateLead.mutateAsync({ id: lead.id, payload: { budgetAmount: lostBudget } });
      }
      await requestChange.mutateAsync({
        id: lead.id,
        payload: {
          toStatusId: pickedStatus.id,
          toSubStatusId: subStatusId || null,
          lostReasonId: lostReasonId || null,
          requestorNote: note.trim() ? note.trim() : null,
        },
      });
      reset();
      setLostBudget(null);
      setShowLostDialog(false);
    } catch {
      // toast surfaced by the mutation's onError.
    }
  };

  // 2026-08-06. Minimal reopen-as-request action for a closed lead (LOST
  // or WON) - SALES_LEAD approval parity, reopen is no longer a dead end
  // for non-admins on either bucket. Files a STATUS_CHANGE request
  // targeting Not Qualified; the backend approve-replay path
  // (bypassLostReopenGate / bypassWonReopenGate) performs the actual
  // reopen once a reviewer approves it - same machinery every other
  // gated request already uses.
  const submitReopenRequest = async (): Promise<void> => {
    if (!notQualifiedStatusId) return;
    try {
      await requestChange.mutateAsync({
        id: lead.id,
        payload: {
          kind: 'STATUS_CHANGE',
          toStatusId: notQualifiedStatusId,
          toSubStatusId: null,
        },
      });
      // Mirrors the backward-move request toast below: a SALES_LEAD's own
      // request has no team lead above it, so it routes to the admin pool.
      toast.success(
        me?.role === 'SALES_LEAD'
          ? 'Reopen request sent to an admin for approval'
          : 'Reopen request sent to your team lead for approval',
      );
    } catch {
      // toast surfaced by the mutation's onError.
    }
  };

  // ----- Pending-approval cue (inbox-only actions) --------------------

  // 2026-07-10. Client-side stale filter (defense-in-depth). The API
  // also filters, but a stale JS bundle or an in-flight cache write
  // could still show a STATUS_CHANGE request whose `from` state no
  // longer matches. Hide those here so the cue is never wrong.
  // LOST / WON / DESTINATION_CHANGE requests are kept regardless -
  // they don't have a "from state" tied to the pipeline.
  const awaitingApproval = resolvePendingLeadApproval(
    lead.pendingStatusChangeRequest,
    lead.currentStatus.id,
  );
  // 2026-07-20. Lead detail never renders the amber review panel; the
  // cue stays a one-line "awaiting" pill so a second submit doesn't 409
  // as a mystery failure. 2026-08-01: reviewers can now open the
  // approvals dialog from that pill instead of walking to the inbox.
  const awaitingApprovalSummary = awaitingApproval
    ? awaitingApproval.kind === 'DESTINATION_CHANGE'
      ? `destination change to ${awaitingApproval.toDestination?.name ?? 'Unknown'}`
      : `move to ${formatStatusWithSubStatus(awaitingApproval.toStatus, awaitingApproval.toSubStatus)}`
    : null;
  // 2026-08-06. A pending LOST_TRANSITION does NOT lock this component -
  // the status changer must stay usable so the user can move the lead /
  // resolve the pending lost (SALES_LEAD approval parity Change B). Every
  // other pending kind (DESTINATION_CHANGE, WON_TRANSITION, STATUS_CHANGE)
  // keeps locking exactly as before. The "Awaiting ... approval" badge
  // above still renders off `awaitingApproval` directly, so it stays
  // visible during a pending LOST even though the dropdown unlocks.
  const approvalLocksActions =
    Boolean(awaitingApproval) && awaitingApproval?.kind !== 'LOST_TRANSITION';

  // ----- Out-of-closed-bucket gate (LOST or WON) -----------------------

  // 2026-07-25 / 2026-08-06. Reopening a closed (LOST or WON) lead directly
  // is ADMIN-ONLY (mirrors `canActDirectly`/`canCloseLeadAsLostDirectly`).
  // SALES_AGENT and SALES_LEAD no longer hit a dead end here though - a
  // pending reopen request shows the same "awaiting approval" cue as the
  // rest of this component, otherwise a "Request reopen" action files one
  // (see `submitReopenRequest`). This is a minimal reopen surface - a
  // single request straight to Not Qualified - not the full status/sub-
  // status picker the OPEN-bucket changer below offers. WON was previously
  // handled one level up: `LeadDetailPage` didn't even mount this
  // component for a non-admin on a WON lead, so there was no reopen path
  // at all; it now mounts unconditionally and this gate covers both
  // buckets the same way.
  const isAdmin = me?.role === 'ADMIN';
  if ((isInLost || isInWon) && !isAdmin) {
    if (awaitingApproval) {
      return (
        <UzBadge tone="secondary" pill>
          Awaiting {awaitingApproval.assignedReviewer ? 'team lead' : 'admin'} approval:{' '}
          {awaitingApprovalSummary}
        </UzBadge>
      );
    }
    return (
      <UzButton
        size="sm"
        variant="outline"
        onClick={() => void submitReopenRequest()}
        loading={requestChange.isPending}
        disabled={!notQualifiedStatusId}
      >
        Request reopen
      </UzButton>
    );
  }

  // ----- Status changer panel -----------------------------------------

  return (
    <>
      {awaitingApproval && (
        <div className="mb-2 inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-xs text-sky-800">
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-sky-500"
              aria-hidden
            />
            <span>
              Awaiting {awaitingApproval.assignedReviewer ? 'team lead' : 'admin'} approval:{' '}
              <span className="font-medium">{awaitingApprovalSummary}</span>
            </span>
          </span>
          {canReviewLeadApprovalRequest(awaitingApproval, me) && (
            <button
              type="button"
              onClick={() => setShowApprovalReview(true)}
              className="font-medium underline underline-offset-2 hover:text-sky-950"
            >
              Review request
            </button>
          )}
        </div>
      )}

      {/* 2026-08-01. Same dialog as `/leads/approvals` - verify the
          request and approve / reject it right here. The inbox link
          stays available in the footer for the full queue. */}
      <LeadApprovalReviewDialog
        request={
          showApprovalReview && awaitingApproval
            ? toApprovalInboxItem(awaitingApproval, lead)
            : null
        }
        onClose={() => setShowApprovalReview(false)}
        footerLeft={
          <Link
            to="/leads/approvals"
            className="text-sm font-medium text-zinc-600 underline underline-offset-2 hover:text-zinc-900"
          >
            Open approvals inbox
          </Link>
        }
      />
      <div className="flex flex-wrap items-center gap-2">
        <div
          // Native tooltip on the wrapper so the disabled control still
          // surfaces the "why" on hover. A wrapper is used (not
          // `title` on UzSelect itself) because Radix's Select trigger
          // relays clicks and would swallow the tooltip on a disabled
          // element in some browsers.
          title={
            approvalLocksActions
              ? 'Status changes are locked while an approval request is pending.'
              : dropdownLocked
                ? 'Log a Follow-up call with Call Status "Connected" to unlock this dropdown.'
                : undefined
          }
        >
          <UzSelect
            name="changeStatus"
            placeholder={stagesQuery.isLoading ? 'Loading…' : 'Query status'}
            options={statusOptions}
            // 2026-07-09. Always pass a string so the underlying Radix
            // Select stays CONTROLLED for the life of the component.
            // Passing `undefined` when the state is empty made the
            // component switch modes (uncontrolled -> controlled) and
            // fired React's "Select is changing from uncontrolled to
            // controlled" warning.
            value={statusId ?? ''}
            onValueChange={(v) => {
              userPickedRef.current = true;
              setStatusId(v ?? '');
              setSubStatusId('');
              submittedTargetRef.current = '';
              // Re-picking the status abandons any in-progress Lost pick -
              // clear the inline Lost remark so it doesn't linger visibly
              // (the payload is also gated on `pickedIsLost`, so a stale
              // note here could never leak into a transition either way).
              setNote('');
            }}
            disabled={pending || stagesQuery.isLoading || dropdownLocked || approvalLocksActions}
          />
        </div>
        {statusId && subStatusesQuery.options.length > 0 && (
          <UzSelect
            name="changeSubStatus"
            placeholder={subStatusesQuery.isLoading ? 'Loading…' : 'Sub-status…'}
            options={subStatusesQuery.options}
            value={subStatusId ?? ''}
            onValueChange={(v) => {
              userPickedRef.current = true;
              setSubStatusId(v ?? '');
              submittedTargetRef.current = '';
              setNote('');
            }}
            disabled={
              pending || subStatusesQuery.isLoading || dropdownLocked || approvalLocksActions
            }
          />
        )}
        {/* The "Log a Connected follow-up to change status" hint pill was
            removed 2026-07-17 - the locked (disabled) dropdown alone signals
            the state; `dropdownLocked` still gates the selects above. */}
        {pickedIsLost && hasStageChange && (
          <UzSelect
            name="lostReasonId"
            placeholder={lostReasonsQuery.isLoading ? 'Loading reasons…' : 'Lost reason'}
            options={lostReasonOptions}
            value={lostReasonId || undefined}
            onValueChange={(v) => {
              userPickedRef.current = true;
              setLostReasonId(v ?? '');
            }}
            disabled={pending || lostReasonsQuery.isLoading}
          />
        )}
        {/* ADMIN direct-into-LOST path has no other note input - the
            non-admin path's note field lives in the request dialog below.
            The server requires a note on a direct into-LOST transition. This
            one combination keeps an explicit Confirm button (see
            `confirmLostDirect`) instead of the auto-submit effect - firing
            off `canSubmit` would submit after the first keystroke here. */}
        {pickedIsLost && canActDirectly && hasStageChange && (
          <div className="w-full basis-full space-y-2">
            <UzTextarea
              name="lostNote"
              label="Reason / remark (required)"
              placeholder="Why is this lead being marked lost?"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={pending}
              required
            />
            <UzButton
              size="sm"
              onClick={() => void confirmLostDirect()}
              loading={pending}
              disabled={!canSubmit || pending}
            >
              Confirm lost
            </UzButton>
          </div>
        )}
        {/* Submit button was removed 2026-06-25; the transition fires
            automatically once the picker tuple is "ready" (see the
            auto-submit useEffect above). A small spinner badge
            surfaces the in-flight state instead. Exception: the ADMIN
            direct-into-LOST note field above, which needs an explicit
            confirm. */}
        {pending && (
          <UzBadge tone="secondary" pill>
            Saving…
          </UzBadge>
        )}
      </div>

      {/* 2026-07-09. Required-fields modal is now the shared
          `LeadMissingFieldsModal` component so this header picker and
          the follow-up "Move to Active" flow open the same UX. The
          host still owns the dismiss vs save distinction via
          `savedFromMissingRef`, so on dismiss we revert the picker
          back to the lead's live status but on save we leave it on
          Active so the auto-submit completes. */}
      <LeadMissingFieldsModal
        open={showMissingModal}
        onOpenChange={(open) => {
          setShowMissingModal(open);
          if (!open) {
            submittedTargetRef.current = '';
            if (savedFromMissingRef.current) {
              savedFromMissingRef.current = false;
            } else {
              syncPickerFromLead(lead);
            }
          }
        }}
        lead={lead}
        // When enforcement is on, evaluate the extended readiness set so
        // the operator sees the travel-intent gaps too.
        mode={travelIntentEnforce ? 'active+travelIntent' : 'active'}
        // 2026-07-09. Preseed the modal's inner Query status / Sub
        // status pickers to the outer header picker's TARGET (what the
        // operator picked here, e.g. "Active" / "Proposal"). Save
        // Changes inside the modal fires the same transition in the
        // same submit round-trip, so the header pill reflects the new
        // stage after one save instead of relying on the auto-submit
        // re-firing after modal close.
        targetStatusId={statusId || null}
        targetSubStatusId={subStatusId || null}
        onReady={() => {
          // The transition ran inside the embedded LeadFormPage save,
          // so the cache already reflects the new stage. Just flag the
          // "saved from modal" state + close.
          savedFromMissingRef.current = true;
          setShowMissingModal(false);
          submittedTargetRef.current = '';
        }}
      />

      {/* Won modal - opens when the operator picks "Won" from the status
          dropdown. Embeds the same lead Edit form used elsewhere so the
          win details can be completed in place. On close without saving
          we revert the picker to the lead's live status so the dropdown
          doesn't stay stuck on "Won". The actual won transition/next
          steps will be wired from here. */}
      <UzDialog
        open={showWonModal}
        onOpenChange={(open) => {
          setShowWonModal(open);
          if (!open) {
            submittedTargetRef.current = '';
            syncPickerFromLead(lead);
          }
        }}
        title="Mark lead as Won"
        description={
          canActDirectly
            ? 'Capture the conversion details to mark this lead as Won.'
            : 'Capture the conversion details. Marking Won needs your team lead / admin to approve.'
        }
        size="lg"
      >
        <WonConversionForm
          lead={lead}
          canWinDirectly={canActDirectly}
          wonStatusId={wonStatusId}
          onDone={(updated) => {
            setShowWonModal(false);
            submittedTargetRef.current = '';
            syncPickerFromLead(updated ?? lead);
          }}
        />
      </UzDialog>

      {/* Lost-request confirmation dialog (non-admin path). The
          operator confirms + adds an optional note, then the request
          is routed to their team lead (SALES_AGENT) or admin pool
          (SALES_LEAD). */}
      <UzDialog
        open={showLostDialog}
        onOpenChange={(open) => {
          setShowLostDialog(open);
          // Dismissed without submitting -> revert the picker so it doesn't
          // stay stuck showing "Lost".
          if (!open) syncPickerFromLead(lead);
        }}
        title="Submit for team lead approval"
        description="Closing a lead as LOST needs your team lead to review. You'll be notified once they decide."
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <UzButton
              variant="ghost"
              onClick={() => {
                setShowLostDialog(false);
                syncPickerFromLead(lead);
              }}
              disabled={pending}
            >
              Cancel
            </UzButton>
            <UzButton
              onClick={submitLostRequest}
              loading={requestChange.isPending || updateLead.isPending}
              disabled={
                pending ||
                note.trim() === '' ||
                (needsBudgetForLost && (lostBudget === null || lostBudget <= 0))
              }
            >
              Send for approval
            </UzButton>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-zinc-700">
          <p>
            Target: <strong>{pickedStatus?.name}</strong>{' '}
            {pickedIsLost && lostReasonOptions.length > 0 && lostReasonId
              ? `(${lostReasonOptions.find((o) => o.value === lostReasonId)?.label ?? ''})`
              : ''}
          </p>
          <UzTextarea
            name="requestorNote"
            label="Reason / remark (required)"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={pending}
            required
            hint="Your team lead sees this when reviewing the request."
          />
          {needsBudgetForLost && (
            <UzNumberInput
              name="lostBudget"
              label="Budget (required for this reason)"
              precision={0}
              value={lostBudget ?? ''}
              onValueChange={(v) => setLostBudget(v ?? null)}
              disabled={pending}
              hint={`"${pickedLostReason?.name}" needs a budget on the lead. It's saved before the request is sent.`}
            />
          )}
        </div>
      </UzDialog>

      {/* Hot-payment-date capture dialog (2026-07-13). Opens when the
          operator moves the lead into Active / Hot with no expected
          payment date already on the lead. The date is required and
          rides along with the transition payload so the two land in
          one round-trip. Dismissing without saving reverts the picker
          so it doesn't stay stuck on Hot. */}
      <UzDialog
        open={showHotDialog}
        onOpenChange={(open) => {
          setShowHotDialog(open);
          if (!open && !hotSavedRef.current) {
            submittedTargetRef.current = '';
            syncPickerFromLead(lead);
            setHotExpectedPaymentDate(null);
          }
          hotSavedRef.current = false;
        }}
        title="Expected payment date"
        description="Marking this lead as Hot means you're close to closing. Capture when payment is expected so reminders and the payments view can pick it up."
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <UzButton
              variant="ghost"
              onClick={() => {
                setShowHotDialog(false);
                submittedTargetRef.current = '';
                syncPickerFromLead(lead);
                setHotExpectedPaymentDate(null);
              }}
              disabled={pending}
            >
              Cancel
            </UzButton>
            <UzButton
              onClick={submitHotDate}
              loading={transition.isPending}
              disabled={!hotExpectedPaymentDate}
            >
              Save and move to Hot
            </UzButton>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-zinc-700">
          <UzDateTimePicker
            mode="date"
            displayFormat="dd/MM/yyyy"
            placeholder="dd/mm/yyyy"
            label="Expected payment date *"
            value={hotExpectedPaymentDate}
            onValueChange={(d) => setHotExpectedPaymentDate(d)}
            disabled={pending}
          />
        </div>
      </UzDialog>

      {/* Lost budget-capture dialog (2026-08-06). Opens on the direct (admin /
          sales-lead) Lost path when the picked reason requires a budget and the
          lead has none. The budget rides along the transition so budget + Lost
          land in one round-trip. Dismissing reverts the picker. */}
      <UzDialog
        open={showBudgetDialog}
        onOpenChange={(open) => {
          setShowBudgetDialog(open);
          if (!open && !budgetSavedRef.current) {
            submittedTargetRef.current = '';
            syncPickerFromLead(lead);
            setLostBudget(null);
          }
          budgetSavedRef.current = false;
        }}
        title="Budget required to mark Lost"
        description={`The reason "${pickedLostReason?.name ?? ''}" needs a budget on the lead. Enter it to close this lead as Lost.`}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <UzButton
              variant="ghost"
              onClick={() => {
                setShowBudgetDialog(false);
                submittedTargetRef.current = '';
                syncPickerFromLead(lead);
                setLostBudget(null);
              }}
              disabled={pending}
            >
              Cancel
            </UzButton>
            <UzButton
              onClick={submitLostWithBudget}
              loading={transition.isPending}
              disabled={lostBudget === null || lostBudget <= 0}
            >
              Save budget and mark Lost
            </UzButton>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-zinc-700">
          <UzNumberInput
            name="lostBudgetDirect"
            label="Budget *"
            precision={0}
            value={lostBudget ?? ''}
            onValueChange={(v) => setLostBudget(v ?? null)}
            disabled={pending}
          />
        </div>
      </UzDialog>
    </>
  );
};

/**
 * Won-conversion form (2026-07-06). Opens from the status dropdown's "Won"
 * pick. Three required, highlighted fields: the itinerary/quote the lead
 * converted on, the agreed sale amount, and the conversion date.
 *
 * `canWinDirectly` = true (ADMIN only as of 2026-08-06) fires the
 * transition immediately. false (SALES_AGENT / SALES_LEAD) submits an
 * approval request routed to the assigned team lead / admin pool.
 */
const WonConversionForm = ({
  lead,
  canWinDirectly,
  wonStatusId,
  onDone,
}: {
  lead: LeadDetail;
  canWinDirectly: boolean;
  wonStatusId: string | null;
  onDone: (updated?: LeadDetail) => void;
}): React.JSX.Element => {
  const transition = useTransitionLeadStage();
  const requestChange = useCreateStatusChangeRequest();
  const pending = transition.isPending || requestChange.isPending;

  const [revisionId, setRevisionId] = useState<string>('');
  const [saleAmount, setSaleAmount] = useState<number | undefined>(undefined);
  const [conversionDate, setConversionDate] = useState<Date | null>(null);
  // 2026-08-26. Manual (external) close - ADMIN only. When on, the win was
  // agreed outside the system: the version picker is hidden and the operator
  // hand-enters sale amount, pre-tax TTV, closing date (`conversionDate`) and
  // payment date. Only rendered when `canWinDirectly` (ADMIN), so the
  // agent/approval flow is completely untouched.
  const [manualClose, setManualClose] = useState<boolean>(false);
  const [ttvAmount, setTtvAmount] = useState<number | undefined>(undefined);
  const [paymentDate, setPaymentDate] = useState<Date | null>(null);
  // First-installment / non-refundable deposit taken up front, captured when an
  // admin marks a lead Won by hand (mirrors what the paid first instalment
  // records on a payment-driven auto-win). Recorded against the full sale value.
  const [wonDepositAmount, setWonDepositAmount] = useState<number | undefined>(undefined);
  const toggleManualClose = (on: boolean): void => {
    setManualClose(on);
    // Clear the other mode's captured values so a half-filled switch can't
    // submit stale data (e.g. a version-derived amount on a manual close).
    if (on) {
      setRevisionId('');
      setSaleAmount(undefined);
    } else {
      setSaleAmount(undefined);
      setTtvAmount(undefined);
      setPaymentDate(null);
    }
  };

  // Published sub-versions only (Task 7's version-history + Won-picker
  // endpoint) - the win must pin to a specific PUBLISHED revision, not just
  // an itinerary, so the server can snapshot the exact price the lead saw.
  const versionsQuery = useItineraryVersions(lead.id);
  const versions = versionsQuery.data ?? [];
  // Won transition needs a PUBLISHED revision (unpublished draft cards are
  // listed too in the versions endpoint now, but they can't be pinned to a
  // sale). Filter to published-only + non-null revisionId.
  const versionOptions = useMemo(
    () =>
      versions
        .filter(
          (v): v is typeof v & { revisionId: string } =>
            v.revisionId !== null && v.revisionId !== undefined,
        )
        .map((v) => ({
          value: v.revisionId,
          label: `${v.label} - ${fmtWonPrice(v.grandTotalPaise)}`,
        })),
    [versions],
  );
  const hasVersions = versionOptions.length > 0;
  // The picker stores only the selected `revisionId`; both WON payloads
  // also need the parent `itineraryId` (Task 10 regression - dropped it
  // from the WON_TRANSITION request, which the shared schema's
  // superRefine still requires).
  const selectedVersion = versions.find((v) => v.revisionId === revisionId);

  // 2026-07-14. Sale amount is derived from the picked version's
  // quotation total (paise / 100 = rupees). The input stays visible
  // (context for the operator) but read-only so ops can't fat-finger
  // a mismatched figure - the WON row on the lead has to match the
  // customer-facing quote. Cleared when the version is cleared.
  useEffect(() => {
    // On a manual close the amount is hand-entered - never derive/clobber it.
    if (manualClose) return;
    if (!selectedVersion) {
      setSaleAmount(undefined);
      return;
    }
    const paise = Number(selectedVersion.grandTotalPaise);
    if (!Number.isFinite(paise)) {
      setSaleAmount(undefined);
      return;
    }
    // Round to 2 decimals to avoid float noise (e.g. 12500.999999).
    setSaleAmount(Math.round(paise) / 100);
  }, [selectedVersion, manualClose]);

  const canSubmit = manualClose
    ? Boolean(wonStatusId) &&
      saleAmount !== undefined &&
      ttvAmount !== undefined &&
      conversionDate !== null &&
      paymentDate !== null
    : Boolean(wonStatusId) &&
      revisionId !== '' &&
      saleAmount !== undefined &&
      conversionDate !== null;

  const submit = async (): Promise<void> => {
    if (!canSubmit || !wonStatusId || !conversionDate) return;
    const toYmd = (d: Date): string =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const ymd = toYmd(conversionDate);
    try {
      if (canWinDirectly) {
        // Manual close (ADMIN only): no version is pinned; sale + TTV amounts
        // and payment date are hand-entered. Closing date reuses `ymd`.
        const payload = manualClose
          ? {
              toStatusId: wonStatusId,
              toSubStatusId: null,
              manualClose: true,
              saleAmount,
              ttvAmount,
              wonDepositAmount,
              conversionDate: ymd,
              expectedPaymentDate: paymentDate ? toYmd(paymentDate) : undefined,
            }
          : {
              toStatusId: wonStatusId,
              toSubStatusId: null,
              convertedItineraryId: selectedVersion?.itineraryId,
              convertedRevisionId: revisionId,
              saleAmount,
              wonDepositAmount,
              conversionDate: ymd,
            };
        const updated = await transition.mutateAsync({
          id: lead.id,
          suppressSuccessToast: true,
          payload,
        });
        toast.success(manualClose ? 'Lead marked as Won (manual close)' : 'Lead marked as Won');
        onDone(updated);
      } else {
        await requestChange.mutateAsync({
          id: lead.id,
          payload: {
            kind: 'WON_TRANSITION',
            toStatusId: wonStatusId,
            convertedItineraryId: selectedVersion?.itineraryId,
            convertedRevisionId: revisionId,
            saleAmount,
            conversionDate: ymd,
          },
        });
        toast.success('Won request sent for approval');
        // The lead refetches with a PENDING request - the parent re-renders
        // the pending banner; just close by syncing to the current lead.
        onDone();
      }
    } catch {
      // toast surfaced by the mutation's onError.
    }
  };

  return (
    <div className="space-y-4">
      {/* Manual (external) close toggle - ADMIN only. Hidden entirely for the
          agent/approval flow, which stays exactly as before. */}
      {canWinDirectly && (
        <UzCheckbox
          checked={manualClose}
          onCheckedChange={(v) => toggleManualClose(v === true)}
          disabled={pending}
          label="Manual close (deal confirmed outside the system)"
          description="Skip the itinerary version and enter the amounts, closing date and payment date by hand. Use only for wins quoted or booked outside the CRM."
        />
      )}
      {/* Highlighted, all-required conversion block. */}
      <div className="space-y-4 rounded-lg border-2 border-amber-300 bg-amber-50/60 p-4">
        {!manualClose && (
          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-900">
              Published version <span className="text-red-600">*</span>
            </label>
            <UzSelect
              name="wonRevision"
              placeholder={
                versionsQuery.isLoading
                  ? 'Loading…'
                  : hasVersions
                    ? 'Select the converted version'
                    : 'No published versions on this lead yet'
              }
              options={versionOptions}
              value={revisionId || undefined}
              onValueChange={(v) => setRevisionId(v ?? '')}
              disabled={pending || versionsQuery.isLoading || !hasVersions}
            />
            {!versionsQuery.isLoading && !hasVersions && (
              <p className="mt-1 text-xs text-amber-800">Publish a version before marking Won.</p>
            )}
          </div>
        )}
        <UzNumberInput
          name="wonSaleAmount"
          label="Sale amount *"
          min={0}
          precision={2}
          value={saleAmount ?? ''}
          onValueChange={(v) => setSaleAmount(v ?? undefined)}
          hint={
            manualClose
              ? 'Agreed sale amount (the whole booking value).'
              : selectedVersion
                ? 'Auto-filled from the selected version’s quotation total.'
                : 'Pick a published version above to auto-fill the sale amount.'
          }
          readOnly={!manualClose}
          disabled={pending || (!manualClose && !selectedVersion)}
        />
        {manualClose && (
          <UzNumberInput
            name="wonTtvAmount"
            label="TTV amount (pre-tax) *"
            min={0}
            precision={2}
            value={ttvAmount ?? ''}
            onValueChange={(v) => setTtvAmount(v ?? undefined)}
            hint="Pre-tax taxable value - this drives TTV / ATV in the reports."
            disabled={pending}
          />
        )}
        <UzDateTimePicker
          mode="date"
          displayFormat="dd/MM/yyyy"
          placeholder="dd/mm/yyyy"
          label={manualClose ? 'Closing date *' : 'Conversion date *'}
          value={conversionDate}
          onValueChange={(d) => setConversionDate(d)}
          disabled={pending}
        />
        {manualClose && (
          <UzDateTimePicker
            mode="date"
            displayFormat="dd/MM/yyyy"
            placeholder="dd/mm/yyyy"
            label="Payment date *"
            value={paymentDate}
            onValueChange={(d) => setPaymentDate(d)}
            disabled={pending}
          />
        )}
        {/* First-installment deposit actually collected up front. Optional, shown
            only for the direct-win (admin) flows; mirrors the paid first
            instalment on a payment-driven auto-win. */}
        {canWinDirectly && (
          <UzNumberInput
            name="wonDepositAmount"
            label="First installment / deposit taken"
            min={0}
            precision={2}
            value={wonDepositAmount ?? ''}
            onValueChange={(v) => setWonDepositAmount(v ?? undefined)}
            hint="Non-refundable booking amount collected now, against the full sale value. Leave blank if none taken yet."
            disabled={pending}
          />
        )}
      </div>
      <div className="flex justify-end gap-2">
        <UzButton variant="ghost" onClick={() => onDone()} disabled={pending}>
          Cancel
        </UzButton>
        <UzButton onClick={submit} loading={pending} disabled={!canSubmit}>
          {canWinDirectly ? 'Mark as Won' : 'Send for approval'}
        </UzButton>
      </div>
    </div>
  );
};
